import { RoleMatrix, ContainerAccess } from './container-access.enum';

export enum PermissionAction {
  ContainersViewMetadata = 'containers_view_metadata',
  ContainersCreateHierarchy = 'containers_create_hierarchy',
  ContainersModifyMetadata = 'containers_modify_metadata',
  ContainersDeleteHierarchy = 'containers_delete_hierarchy', // session delete
  ContainersDeleteProject = 'containers_delete_project',
  CopyByReference = 'copy_by_reference',
  AnalysesViewMetadata = 'analyses_view_metadata',
  AnalysesCreateSdk = 'analyses_create_sdk',
  AnalysesCreateJob = 'analyses_create_job',
  AnalysesModifyMetadata = 'analyses_modify_metadata',
  AnalysesDelete = 'analyses_delete',
  FilesViewMetadata = 'files_view_metadata',
  FilesViewContents = 'files_view_contents',
  FilesDownload = 'files_download',
  FilesCreateUpload = 'files_create_upload',
  FilesCreateSingle = 'files_create_single',
  FilesCreateBulk = 'files_create_bulk',
  FilesModifyMetadata = 'files_modify_metadata',
  FilesMove = 'files_move',
  FilesDeleteNonDeviceData = 'files_delete_non_device_data',
  FilesDeleteDeviceData = 'files_delete_device_data',
  TagsView = 'tags_view',
  TagsManage = 'tags_manage',
  NotesView = 'notes_view',
  NotesManage = 'notes_manage',
  ProjectPermissionsView = 'project_permissions_view',
  ProjectPermissionsManage = 'project_permissions_manage',
  GearRulesView = 'gear_rules_view',
  GearRulesManage = 'gear_rules_manage',
  DataViewsView = 'data_views_view',
  DataViewsManage = 'data_views_manage',
  SessionTemplatesView = 'session_templates_view',
  SessionTemplatesManage = 'session_templates_manage',
  SettingsView = 'settings_view',
  SettingsModify = 'settings_modify',
  ProjectSharingManage = 'project_sharing_manage',
  JobsView = 'jobs_view',
  JobsRunCancel = 'jobs_run_cancel', // remove when job_prioritization feature flag is retired
  JobsCancelAny = 'jobs_cancel_any', // remove when job_prioritization feature flag is retired
  JobsManage = 'jobs_manage',
  JobsManageOthers = 'jobs_manage_others',
  ReaderTaskAdmin = 'reader_task_admin',
  ReaderTaskView = 'reader_task_view',
  AnnotationsManage = 'annotations_manage',
  AnnotationsViewOthers = 'annotations_view_others',
  AnnotationsEditOthers = 'annotations_edit_others',
  FormAdmin = 'form_admin',
  FormResponsesManage = 'form_responses_manage',
  FormResponsesViewOthers = 'form_responses_view_others',
  FormResponsesEditOthers = 'form_responses_edit_others',
  ImportsManage = 'imports_manage',
  ExportsManage = 'exports_manage',
  JupyterLabServersRead = 'jupyterlab_servers_read',
  JupyterLabServersLaunch = 'jupyterlab_servers_launch',
  JupyterLabServersCreate = 'jupyterlab_servers_create',
  JupyterLabServersModify = 'jupyterlab_servers_modify',
  JupyterLabServersDelete = 'jupyterlab_servers_delete',
  AuditTrailReportsManage = 'audit_trail_reports_manage',
  AuditTrailReportsView = 'audit_trail_reports_view',
  TasksAssign = 'tasks_assign',
  TasksCreate = 'tasks_create',
  TasksModify = 'tasks_modify',
  TasksView = 'tasks_view',
  TasksReportCreate = 'tasks_report_create',
  TasksReportDelete = 'tasks_report_delete',
  TasksReportRead = 'tasks_report_read',
}

export const writePermissionActions = new Set([
  PermissionAction.ContainersCreateHierarchy,
  PermissionAction.ContainersModifyMetadata,
  PermissionAction.ContainersDeleteHierarchy,
  PermissionAction.ContainersDeleteProject,
  PermissionAction.CopyByReference,
  PermissionAction.AnalysesCreateSdk,
  PermissionAction.AnalysesCreateJob,
  PermissionAction.AnalysesModifyMetadata,
  PermissionAction.AnalysesDelete,
  PermissionAction.AuditTrailReportsManage,
  PermissionAction.FilesCreateUpload,
  PermissionAction.FilesModifyMetadata,
  PermissionAction.FilesMove,
  PermissionAction.FilesDeleteNonDeviceData,
  PermissionAction.FilesDeleteDeviceData,
  PermissionAction.TagsManage,
  PermissionAction.NotesManage,
  PermissionAction.ProjectPermissionsManage,
  PermissionAction.GearRulesManage,
  PermissionAction.DataViewsManage,
  PermissionAction.SessionTemplatesManage,
  PermissionAction.SettingsModify,
  PermissionAction.ProjectSharingManage,
  PermissionAction.JobsRunCancel, // remove when `job_prioritization` feature flag is retired
  PermissionAction.JobsCancelAny, // remove when `job_prioritization` feature flag is retired
  PermissionAction.JobsManage,
  PermissionAction.JobsManageOthers,
  PermissionAction.ReaderTaskAdmin,
  PermissionAction.AnnotationsManage,
  PermissionAction.AnnotationsEditOthers,
  PermissionAction.FormAdmin,
  PermissionAction.FormResponsesManage,
  PermissionAction.FormResponsesEditOthers,
  PermissionAction.ImportsManage,
  PermissionAction.ExportsManage,
  PermissionAction.JupyterLabServersLaunch,
  PermissionAction.JupyterLabServersCreate,
  PermissionAction.JupyterLabServersModify,
  PermissionAction.JupyterLabServersDelete,
  PermissionAction.TasksAssign,
  PermissionAction.TasksCreate,
  PermissionAction.TasksModify,
  PermissionAction.TasksReportCreate,
  PermissionAction.TasksReportDelete,
]);

/**
 * These are permissions that should be subtracted from the calculated
 * project permission set if a project is in a `locked` state.
 * When adding new permissions, consider adding to this set as well.
 */
export const lockedProjectProhibitedActions = new Set([
  PermissionAction.ContainersCreateHierarchy,
  PermissionAction.ContainersModifyMetadata,
  PermissionAction.ContainersDeleteHierarchy,
  PermissionAction.ContainersDeleteProject,
  PermissionAction.AnalysesCreateSdk,
  PermissionAction.AnalysesCreateJob,
  PermissionAction.AnalysesModifyMetadata,
  PermissionAction.AnalysesDelete,
  PermissionAction.FilesCreateUpload,
  PermissionAction.FilesCreateSingle,
  PermissionAction.FilesCreateBulk,
  PermissionAction.FilesModifyMetadata,
  PermissionAction.FilesMove,
  PermissionAction.FilesDeleteNonDeviceData,
  PermissionAction.FilesDeleteDeviceData,
  PermissionAction.TagsManage,
  PermissionAction.NotesManage,
  PermissionAction.GearRulesManage,
  PermissionAction.DataViewsManage,
  PermissionAction.SessionTemplatesManage,
  PermissionAction.JobsRunCancel, // remove when `job_prioritization` feature flag is retired
  PermissionAction.JobsCancelAny, // remove when `job_prioritization` feature flag is retired
  PermissionAction.JobsManage,
  PermissionAction.JobsManageOthers,
]);

const accessMap = {
  [ContainerAccess.ReadOnly]: PermissionAction.ContainersViewMetadata,
  [ContainerAccess.ReadWrite]: PermissionAction.ContainersCreateHierarchy,
  [ContainerAccess.Admin]: PermissionAction.ProjectPermissionsManage,
};

const collectionsAccessMap = {
  [ContainerAccess.ReadOnly]: PermissionAction.ContainersViewMetadata,
  [ContainerAccess.ReadWrite]: PermissionAction.FilesCreateSingle,
  [ContainerAccess.Admin]: PermissionAction.FilesCreateSingle,
}

export function permissionForCollection(access: ContainerAccess): PermissionAction {
  return collectionsAccessMap[access];
}

export function getRoleMatrix(permissions: PermissionAction[]): RoleMatrix {
  return {
    [ContainerAccess.ReadOnly]: permissions.includes(accessMap[ContainerAccess.ReadOnly]),
    [ContainerAccess.ReadWrite]: permissions.includes(accessMap[ContainerAccess.ReadWrite]),
    [ContainerAccess.Admin]: permissions.includes(accessMap[ContainerAccess.Admin]),
  };
}
